<template>
  <VContainer
    fluid
    class="page-container"
  >
    <!-- Loading overlay -->
    <VOverlay
      :model-value="loading"
      absolute
      class="d-flex align-center justify-center"
      style="position: fixed; top: 0; left: 0; right: 0; bottom: 0;"
    >
      <div class="text-center pa-8">
        <VProgressCircular 
          indeterminate 
          color="primary" 
          size="64" 
          width="6"
          class="mb-6"
        />
        <div class="text-h6 font-weight-bold text-primary mb-2">
          {{ currentLoadingMessage }}
        </div>
        <div class="text-body-2 text-grey-darken-1">
          Max Profit is working his culinary magic...
        </div>
      </div>
    </VOverlay>

    <!-- Metrics cards -->
    <VRow
      v-if="summary"
      dense
    >
      <VCol
        v-for="card in cards"
        :key="card.label"
        cols="12"
        sm="6"
        md="3"
      >
        <VCard elevation="2">
          <VCardTitle class="text-subtitle-1 font-weight-bold">
            {{ card.label }}
          </VCardTitle>
          <VCardText class="text-h6 font-weight-medium">
            {{ card.value }}
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Charts -->
    <VRow
      v-if="summary"
      class="mt-6"
      dense
    >
      <VCol
        cols="12"
        md="6"
      >
        <VCard elevation="2">
          <VCardTitle class="text-subtitle-1 font-weight-bold">
            Sales · COGS · Profit
          </VCardTitle>
          <VCardText>
            <Apexchart
              height="350"
              type="bar"
              :options="salesChartOptions"
              :series="salesChartSeries"
            />
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="12"
        md="6"
      >
        <VCard elevation="2">
          <VCardTitle class="text-subtitle-1 font-weight-bold">
            Conversion Rate
          </VCardTitle>
          <VCardText>
            <Apexchart
              height="350"
              type="radialBar"
              :options="conversionChartOptions"
              :series="conversionChartSeries"
            />
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Empty State -->
    <VRow v-if="!loading && !summary">
      <VCol cols="12">
        <VCard
          elevation="2"
          class="text-center pa-8"
        >
          <VIcon
            size="64"
            color="grey-lighten-1"
            class="mb-4"
          >
            tabler-bulb-off
          </VIcon>
          <div class="text-h6 text-grey-darken-1 mb-2">
            No AI Summary Available
          </div>
          <div class="text-body-2 text-grey">
            No AI-generated summary was found. Try selecting a different date range or store.
          </div>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>

<script setup>
// IF YOU NEED TO USE TYPESCRIPT, USE <script lang="ts" setup> INSTEAD BUT JAVASCRIPT IS PREFERRED.
import { definePage } from 'unplugin-vue-router/runtime'
import { computed, onMounted, ref } from 'vue'
import axios from '../../../services/axios'

definePage({
  meta: {
    requiresAuth: true,
  },
})

// ---------- State ----------
const loading = ref(true)
const summary = ref(null)

// Loading messages
const loadingMessages = ref([
  "Hold tight! Max Profit is in the back making sure your data is well-done, not half-baked.",
  "Prepping your insights with all the right toppings—Max Profit style. Don't go anywhere!",
  "Max Profit is seasoning your data to perfection. Almost ready to serve!",
  "Cooking up some fresh insights! Max Profit never serves day-old data.",
  "Max Profit is whisking together your analytics—no lumps, just smooth insights!",
  "Grilling your data to get those perfect insight char marks. Max Profit takes his time!",
  "Max Profit is plating your insights with garnish and all. Presentation matters!",
  "Slow-cooking your data for maximum flavor. Max Profit knows good things take time!",
  "Max Profit is checking the temperature on your insights—they're almost perfectly done!",
  "Marinating your data in special spices. Max Profit's secret recipe takes a moment!",
  "Max Profit is tenderizing those tough data points. Your insights will melt in your mouth!",
  "Adding the final touches to your data soufflé. Max Profit doesn't rush perfection!",
])

// Use first message to avoid infinite re-renders from Math.random()
const currentLoadingMessage = computed(() => loadingMessages.value[0])

// ---------- Fetch data on mount ----------
async function fetchSummary() {
  try {
    const { data } = await axios.get('/api/insights/aisummary')  // HERE YOU CAN ALSO PASS START DATE AND END DATE AS QUERY PARAMS

    summary.value = data?.[0] ?? null
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Insight summary fetch error →', err)
  } finally {
    loading.value = false
  }
}

onMounted(fetchSummary)

// ---------- Derived data ----------
const profit = computed(() => {
  if (!summary.value) return 0
  
  return Number(summary.value.TotalSales) - Number(summary.value.TotalCOGS)
})

// Cards content
const cards = computed(() => {
  if (!summary.value) return []
  
  return [
    {
      label: 'Total Sales',
      value: `$${Number(summary.value.TotalSales).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
    },
    {
      label: 'Total Waste',
      value: `$${Number(summary.value.TotalWaste).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
    },
    { label: 'Units Made', value: Number(summary.value.TotalUnitsMade).toLocaleString() },
    { label: 'Units Sold', value: Number(summary.value.TotalUnitsSold).toLocaleString() },
    { label: 'Total Traffic', value: Number(summary.value.TotalTraffic).toLocaleString() },
    { label: 'Avg Daily Traffic', value: Number(summary.value.AverageDailyTraffic).toFixed(1) },
    { label: 'Profit Margin %', value: `${Number(summary.value.ProfitMarginPercent).toFixed(2)}%` },
    { label: 'Conversion Rate %', value: `${Number(summary.value.ConversionRate).toFixed(2)}%` },
  ]
})

// Sales vs COGS vs Profit chart
const salesChartOptions = computed(() => ({
  chart: { toolbar: { show: false } },
  xaxis: { categories: ['Sales', 'COGS', 'Profit'] },
  dataLabels: { enabled: false },
  plotOptions: { bar: { columnWidth: '45%' } },
  yaxis: {
    labels: {
      formatter: val => `$${val.toLocaleString()}`,
    },
  },
}))

const salesChartSeries = computed(() => [
  {
    name: 'USD',
    data: summary.value
      ? [Number(summary.value.TotalSales), Number(summary.value.TotalCOGS), profit.value]
      : [],
  },
])

// Conversion radial chart
const conversionChartOptions = computed(() => ({
  plotOptions: {
    radialBar: {
      hollow: { size: '60%' },
      dataLabels: {
        value: {
          formatter: val => `${val.toFixed(1)}%`,
        },
      },
    },
  },
  labels: ['Conversion'],
  chart: { toolbar: { show: false } },
}))

const conversionChartSeries = computed(() => [summary.value ? Number(summary.value.ConversionRate) : 0])
</script>

<style scoped>
.v-card {
    min-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
}
</style>
